import { useQueryClient } from "@tanstack/react-query"
import { CitiesService, CityPublic } from "../../client"
import { useState } from "react"
import { Field, FieldRequiredIndicator, GridItem, useListCollection } from "@chakra-ui/react"
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetValue } from "react-hook-form"
import { useAsync } from "react-use"
import CityField from "./CityField"
import PostcodeField from "./PostcodeField"

interface CityZipProps {
    namePrefix: string
    requiredPostcode: boolean
    requiredCity: boolean
    form: {
        control: Control<any>
        register: UseFormRegister<any>
        setValue: UseFormSetValue<any>
        errors: FieldErrors<any>
        clearErrors: UseFormClearErrors<any>
    }
}

function getCityZipQueryOptions({
    city_name,
    postcode,
}: {
    city_name: string | null,
    postcode: string | null,
}) {
    return {
        queryFn: async () => {
        const data = await CitiesService.readCities({
            cityName: city_name,
            postcode: postcode,
        })

        return data ?? { data: null, count: 0 }
        },
        queryKey: ["cities", { city_name, postcode }],
    }
}

const CitiesFields = ({namePrefix, requiredPostcode, requiredCity, form} : CityZipProps) => {
    const queryClient = useQueryClient()
    
    const [cityName, setCityName] = useState<string | null>(null)
    const [postcode, setPostcode] = useState<string | null>(null)

    const { collection: cityCollection, set: citySet } = useListCollection<CityPublic>({
        initialItems: [],
        itemToString: (item) => item.name,
        itemToValue: (item) => `${item.postcode} - ${item.name}`,
    })

    const { collection: postcodeCollection, set: postcodeSet } = useListCollection<CityPublic>({
        initialItems: [],
        itemToString: (item) => item.postcode,
        itemToValue: (item) => `${item.postcode} - ${item.name}`,
    })

    const statePostcode = useAsync(async () => {
        if(postcode) {
            queryClient.fetchQuery(getCityZipQueryOptions({city_name: null, postcode: postcode})).then((e) => {
                if(e.data){ 
                    postcodeSet(e.data)
                    citySet(e.data)
                }
            })
        }
    }, [postcode, postcodeSet])

    const stateCity = useAsync(async () => {
        if(cityName) {
            queryClient.fetchQuery(getCityZipQueryOptions({city_name: cityName, postcode: null})).then((e) => {
                if(e.data){ 
                    citySet(e.data)
                    postcodeSet(e.data)
                }
            })
        }
    }, [cityName, citySet])

    const setValuesSelected = (value: string) => {
        if(value) {
            const plz = value.split(" -")[0]
            const city = value.split("- ")[1]
            setPostcode(plz)
            form.setValue(`${namePrefix}city`, city) 
            form.clearErrors(`${namePrefix}city`)
        }
    }

    return (
        <>
            <GridItem colSpan={{base: 2, md: 1}}>
                <Field.Root {...(requiredPostcode && { required: true })} invalid={!!form.errors.postcode} mt={6}>
                    <Field.Label htmlFor="postcode">
                        PLZ
                        {requiredPostcode &&
                            <FieldRequiredIndicator />
                        }
                    </Field.Label>

                    <Controller
                        control={form.control}
                        name={`${namePrefix}postcode`}
                        render={({ field }) => (
                            <PostcodeField
                                collection={postcodeCollection}
                                loading={statePostcode.loading}
                                field={field}
                                onInputChange={setPostcode}
                                onValueChange={setValuesSelected} 
                                register={form.register}
                                isRequired={requiredPostcode}
                            />
                        )}
                     />

                    {form.errors?.postcode && (

                        <Field.ErrorText>{`${form.errors?.postcode?.message}`}</Field.ErrorText>
                    )}
                </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
                <Field.Root {...(requiredCity && { required: true })} invalid={!!form.errors.city} mt={6}>
                    <Field.Label htmlFor="city">
                        Ort
                        {requiredCity &&
                            <FieldRequiredIndicator />
                        }
                    </Field.Label>

                    <Controller
                        control={form.control}
                        name={`${namePrefix}city`}
                        render={({ field }) => (
                            <CityField
                                collection={cityCollection}
                                loading={stateCity.loading}
                                field={field}
                                onInputChange={setCityName}
                                register={form.register}
                                isRequired={requiredCity}
                            />
                        )}
                     />
                    {form.errors?.city && (
                        <Field.ErrorText>{`${form.errors?.city?.message}`}</Field.ErrorText>
                    )}
                </Field.Root>
            </GridItem>
        </>
    )
}

export default CitiesFields
